import {
    Card, Collapse, ConfigProvider, theme as AntdTheme, Typography,
} from 'antd';
import { useEffect, useMemo } from 'react';

import { DARK_THEME_HTML_BACKGROUND_COLOR } from '@/constants';
import { useUIStore } from '@/stores';
import { lightTheme } from '@/themes';
import { ProjectField } from '@/types';

type FieldsListProps = {
    title?: string;
    fields: ProjectField[] | null;
    renderAsHtml?: boolean;
    defaultOpenNames?: string[];
};

export const FieldsList = ({ title, fields, renderAsHtml = false, defaultOpenNames }: FieldsListProps) => {
    const { theme: currentTheme } = useUIStore();

    const lightThemeToken = useMemo(() => {
        return AntdTheme.getDesignToken({
            algorithm: AntdTheme.defaultAlgorithm,
            ...lightTheme,
        });
    }, []);

    const defaultActiveKey = useMemo(() => {
        if (!defaultOpenNames?.length || !fields) return [];
        return fields
            .map((field, index) => ({ key: `field-${index}-${field.name}`, name: field.name }))
            .filter(({ name }) => defaultOpenNames.some(n => n.toLowerCase() === name.toLowerCase()))
            .map(({ key }) => key);
    }, [fields, defaultOpenNames]);

    if (!fields || fields.length === 0) {
        return null;
    }

    const items = fields.map((field, index) => ({
        key: `field-${index}-${field.name}`,
        label: (
            <Typography.Text strong>{field.name}</Typography.Text>
        ),
        children: renderAsHtml ? (
            <ConfigProvider
                theme={{
                    algorithm: currentTheme === 'dark' ? AntdTheme.defaultAlgorithm : undefined,
                    token: currentTheme === 'dark' ? {
                        colorText: lightThemeToken.colorText,
                        colorLink: lightThemeToken.colorLink,
                    } : undefined,
                }}
            >
                <div
                    dangerouslySetInnerHTML={{ __html: field.value || '-' }}
                    style={{
                        padding: '8px 0',
                        color: currentTheme === 'dark' ? lightThemeToken.colorText : undefined,
                    }}
                />
            </ConfigProvider>
        ) : (
            <Typography.Text>{field.value || '-'}</Typography.Text>
        ),
    }));

    const collapseContentBg = currentTheme === 'dark' ? DARK_THEME_HTML_BACKGROUND_COLOR : undefined;

    const collapse = (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        contentBg: collapseContentBg,
                    },
                },
            }}
        >
            <Collapse items={items} defaultActiveKey={defaultActiveKey} />
        </ConfigProvider>
    );

    if (title) {
        return (
            <Card title={title} size="small">
                {collapse}
            </Card>
        );
    }

    return collapse;
};
